import type React from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';
import { size } from '../../layout/helpers';
import { ButtonReset, inputPlaceholder } from '../../layout/reset';

export const animationDuration = 200;

const WrapperComponent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  (props, ref) => {
    return <div ref={ref} {...props} />;
  },
);

export const Wrapper = styled(WrapperComponent).attrs(() => ({
  role: 'dialog',
}))`
  --gs-gutter-block: ${size(2)};
  --gs-gutter-inline: ${size(3)};
  --_wrapper-padding-inline: ${size(2)};

  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: var(--_wrapper-padding-inline) ${size(2)};
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: ${(props) => props.theme.zindex.globalSearch};
  isolation: isolate;

  ${(props) => props.theme.breakpoint.Md} {
    --_wrapper-padding-inline: max(5vh, ${size(2)});
  }
`;

export const Header = styled.header`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${size(1.5)};
  padding: var(--gs-gutter-block) var(--gs-gutter-inline);
`;

export const Search = styled.div.attrs(() => ({
  role: 'search',
}))`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: ${size(1.5)};
  height: ${size(6)};
  cursor: text;
`;

export const Icon = styled.label.attrs(() => ({
  'aria-hidden': true,
}))`
  display: flex;
  align-items: center;
  color: var(--c-text-muted);
  cursor: text;
`;

export const Input = styled.input.attrs(() => ({
  type: 'search',
}))`
  ${inputPlaceholder};
  flex: 1 1 auto;
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
  background-color: transparent;
  font-size: 16px;
  border: none;
  border-radius: inherit;
  box-shadow: none;
  appearance: none;

  &:focus,
  &:focus-visible {
    outline: none;
  }

  // Clears the ‘X’ from Internet Explorer
  &::-ms-clear {
    display: none;
    width: 0;
    height: 0;
  }

  &::-ms-reveal {
    display: none;
    width: 0;
    height: 0;
  }

  // Clears the ‘X’ from Chrome
  &::-webkit-search-decoration,
  &::-webkit-search-cancel-button,
  &::-webkit-search-results-button,
  &::-webkit-search-results-decoration {
    display: none;
  }
`;

export const CloseBtn = styled(ButtonReset)`
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${size(5)};
  height: ${size(5)};
  font-size: ${size(3)};
  border-radius: 50%;
  isolation: isolate;
  transition: transform 200ms ease;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-color: var(--c-accent-bg);
    z-index: -1;
    opacity: 0;
    transition: opacity 200ms ease;
  }

  &:hover {
    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const Result = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${size(3)};
  padding: var(--gs-gutter-block) var(--gs-gutter-inline);
  border-top: 1px solid var(--c-border-main);
  overflow: auto;
  overscroll-behavior: contain;

  &:empty {
    display: none;
  }
`;

export const Window = styled.div`
  --_animation-offset-y: ${size(2)};

  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${size(82)};
  max-height: 100%;
  background-color: var(--c-global-search-bg);
  color: var(--c-global-search-fg);
  border-radius: ${size(2)};
  transition:
    transform ${animationDuration}ms ease,
    opacity ${animationDuration}ms ease;

  .global-search-appear & {
    transform: translateY(var(--_animation-offset-y));
    opacity: 0;
  }

  .global-search-appear-active & {
    transform: translateY(0);
    opacity: 1;
  }

  .global-search-exit & {
    transform: translateY(0);
    opacity: 1;
  }

  .global-search-exit-active & {
    transform: translateY(var(--_animation-offset-y));
    opacity: 0;
  }
`;

export const UiBlock = styled.div.attrs(() => ({
  'aria-hidden': true,
  role: 'button',
}))`
  position: absolute;
  inset: 0;
  background-color: var(--c-global-search-overlay);
  transition: opacity ${animationDuration}ms ease;
  z-index: -1;

  .global-search-appear & {
    opacity: 0;
  }

  .global-search-appear-active & {
    opacity: 1;
  }

  .global-search-exit & {
    opacity: 1;
  }

  .global-search-exit-active & {
    opacity: 0;
  }
`;
